import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, Clock, CheckCircle, XCircle, DollarSign } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISODashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    declinedApplications: 0,
    totalCommissions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch my applications
        const applicationsResponse = await axios.get(`${API}/deal-applications/my-applications`, { 
          headers: getAuthHeaders() 
        }).catch(() => ({ data: [] }));
        const applications = applicationsResponse.data || [];
        
        // Fetch commission balance
        const commissionResponse = await axios.get(`${API}/commissions/my-balance`, { 
          headers: getAuthHeaders() 
        }).catch(() => ({ data: { total_earned: 0 } }));
        
        setStats({
          totalApplications: applications.length,
          pendingApplications: applications.filter(app => 
            app.status === 'Pending' || app.status === 'Under Review'
          ).length,
          approvedApplications: applications.filter(app => 
            app.status === 'Approved'
          ).length,
          declinedApplications: applications.filter(app => 
            app.status === 'Rejected' || app.status === 'Declined'
          ).length,
          totalCommissions: commissionResponse.data?.total_earned || 0,
        });
      } catch (error) {
        console.error('Failed to fetch ISO stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAuthHeaders]);

  const statCards = [
    {
      title: 'Total Applications',
      value: stats.totalApplications,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Pending Review',
      value: stats.pendingApplications,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Approved',
      value: stats.approvedApplications,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Declined',
      value: stats.declinedApplications,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      title: 'Total Commissions',
      value: formatCurrency(stats.totalCommissions),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">ISO Dashboard</h2>
        <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/submit-application" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">Submit Application</p>
                <p className="text-sm text-gray-500">New deal application</p>
              </div>
            </a>
            <a href="/my-applications" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <Clock className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium">My Applications</p>
                <p className="text-sm text-gray-500">View application status</p>
              </div>
            </a>
            <a href="/commissions" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="font-medium">Commissions</p>
                <p className="text-sm text-gray-500">View earnings</p>
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ISODashboard;
